/**
 * Privacy Policy Page
 * Accessible by all users (authenticated and unauthenticated)
 */

import React from 'react';
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBTypography,
  MDBIcon,
} from 'mdb-react-ui-kit';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-policy-page">
      <MDBContainer className="py-5">
        <MDBCard className="policy-card">
          <MDBCardBody className="p-4 p-md-5">
            <div className="text-center mb-4">
              <MDBIcon fas icon="shield-alt" size="3x" className="text-primary mb-3" />
              <MDBTypography tag="h1" className="h2 fw-bold">
                Privacy Policy
              </MDBTypography>
              <p className="text-muted">Last updated: January 19, 2026</p>
            </div>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                1. Introduction
              </MDBTypography>
              <p>
                Welcome to SolidRange. We respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, and safeguard your information when you use our 
                shooting range scoring application.
              </p>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                2. Information We Collect
              </MDBTypography>
              <p>We collect the following types of information:</p>
              <ul>
                <li>
                  <strong>Account Information:</strong> When you sign in with Google, we receive your name, 
                  email address, and profile picture.
                </li>
                <li>
                  <strong>Session Data:</strong> We store your shooting session data, including scores, 
                  target images, shot locations, and timestamps.
                </li>
                <li>
                  <strong>Device Information:</strong> We collect device identifiers to enable synchronization 
                  across your devices.
                </li>
                <li>
                  <strong>Location Data:</strong> If you grant permission, we may collect location data 
                  associated with your shooting sessions.
                </li>
              </ul>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                3. How We Use Your Information
              </MDBTypography>
              <p>We use the collected information to:</p>
              <ul>
                <li>Provide and maintain the SolidRange service</li>
                <li>Synchronize your data across devices</li>
                <li>Analyze your shooting performance and provide statistics</li>
                <li>Process target images using AI for shot detection</li>
                <li>Improve our services and user experience</li>
                <li>Communicate with you about service updates</li>
              </ul>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                4. Data Storage and Security
              </MDBTypography>
              <p>
                Your data is stored securely on Microsoft Azure servers. We implement appropriate technical 
                and organizational measures to protect your personal data against unauthorized access, 
                alteration, disclosure, or destruction.
              </p>
              <p>
                Session data is stored locally on your device and synchronized with our cloud servers when 
                you have an internet connection. You can manage your data through the Settings page.
              </p>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                5. Data Sharing
              </MDBTypography>
              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share 
                your data only in the following circumstances:
              </p>
              <ul>
                <li>With your explicit consent</li>
                <li>To comply with legal obligations</li>
                <li>With service providers who assist in operating our application (e.g., cloud hosting, AI processing)</li>
              </ul>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                6. Your Rights
              </MDBTypography>
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your data</li>
                <li>Export your data</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p>
                You can manage most of these options directly through the Settings page in the application.
              </p>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                7. Cookies and Tracking
              </MDBTypography>
              <p>
                We use essential cookies to maintain your session and preferences. We do not use 
                third-party tracking cookies or advertising cookies.
              </p>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                8. Children's Privacy
              </MDBTypography>
              <p>
                SolidRange is not intended for use by children under the age of 13. We do not knowingly 
                collect personal information from children under 13.
              </p>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                9. Changes to This Policy
              </MDBTypography>
              <p>
                We may update this privacy policy from time to time. We will notify you of any changes 
                by posting the new privacy policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="policy-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                10. Contact Us
              </MDBTypography>
              <p>
                If you have any questions about this privacy policy or our data practices, please contact 
                us through the application's support channels.
              </p>
            </section>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    </div>
  );
};

export default PrivacyPolicy;
